/**
 * Atom: Button
 *
 * Variants: primary | secondary | ghost | danger
 * Sizes:    sm | md | lg
 *
 * Usage:
 *   <Button variant="primary" onClick={handleSave}>Save Project</Button>
 *   <Button variant="danger" size="sm" loading>Deleting...</Button>
 */
import React from 'react'
import { tokens } from '@/design-system/tokens'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
  leftIcon?: React.ReactNode
  icon?: React.ReactNode
}

const variantStyles: Record<string, React.CSSProperties> = {
  primary: {
    background: tokens.color.brand.fluoro,
    color: tokens.color.brand.navy,
    border: `1px solid ${tokens.color.brand.fluoroDark}`,
  },
  secondary: {
    background: tokens.color.background.elevated,
    color: tokens.color.text.body,
    border: `1px solid ${tokens.color.border.hairline}`,
  },
  ghost: {
    background: 'transparent',
    color: tokens.color.text.secondary,
    border: '1px solid transparent',
  },
  danger: {
    background: tokens.color.feedback.error,
    color: tokens.color.neutral.white,
    border: `1px solid ${tokens.color.feedback.error}`,
  },
  success: {
    background: tokens.color.feedback.success,
    color: tokens.color.neutral.white,
    border: `1px solid ${tokens.color.feedback.success}`,
  },
}

// Phase-3 spec §06.1 — geometry, not just color:
//   sm  = 28px minHeight, 12.5px / padding 5×10  (compact rows, web tables)
//   md  = 36px minHeight, 14px   / padding 8×14  (web default)
//   lg  = 44px minHeight, 15px   / padding 11×22 (mobile default — gloves-on hit target)
const sizeStyles: Record<string, React.CSSProperties> = {
  sm: { padding: '5px 10px',  fontSize: '12.5px', minHeight: 28, lineHeight: 1.2 },
  md: { padding: '8px 14px',  fontSize: '14px',   minHeight: 36, lineHeight: 1.2 },
  lg: { padding: '11px 22px', fontSize: '15px',   minHeight: 44, lineHeight: 1.2 },
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  leftIcon,
  icon,
  children,
  disabled,
  style,
  className,
  ...rest
}) => {
  const isDisabled = disabled || loading

  // D1a — disabled = 50% opacity; loading = 85% opacity (spinner visible
  // through faded label rather than blanking the surface).
  const opacity = disabled ? 0.5 : loading ? 0.85 : 1

  const baseStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacing[2],
    fontFamily: tokens.typography.fontFamily.sans,
    fontWeight: tokens.typography.fontWeight.medium,
    borderRadius: tokens.radius.base,
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    opacity,
    transition: `background-color 120ms ease, border-color 120ms ease, color 120ms ease, transform 60ms ease`,
    whiteSpace: 'nowrap',
    outline: 'none',
    ...variantStyles[variant],
    ...sizeStyles[size],
    ...style,
  }

  // D1a focus ring is the locked spec (2px gold outline @ 2px offset);
  // hover lift is a 1px translation. Implemented via a stable className
  // so the class lookup happens once and the styles are simple CSS.
  return (
    <>
      <style>{`
        button.ml-btn-D1a:focus-visible {
          outline: 2px solid var(--brand-gold);
          outline-offset: 2px;
        }
        button.ml-btn-D1a:not([disabled]):hover {
          transform: translateY(-1px);
          filter: brightness(1.05);
        }
        button.ml-btn-D1a:not([disabled]):active {
          transform: translateY(0);
          filter: brightness(0.97);
        }
      `}</style>
      <button
        style={baseStyle}
        disabled={isDisabled}
        className={['ml-btn-D1a', className].filter(Boolean).join(' ')}
        {...rest}
      >
        {loading ? <Spinner size={14} /> : (leftIcon || icon)}
        {children}
      </button>
    </>
  )
}

// Inline spinner for button loading states
const Spinner: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    style={{ animation: 'spin 0.7s linear infinite' }}
  >
    <circle
      cx="8" cy="8" r="6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeDasharray="28 8"
    />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </svg>
)
